import React, { FC, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button, InfoBlock, Table } from "../../../../../../components";
import ModalVariables from "../../../../../../components/modal-alerts";
import { InfoContainer, InfoTitle } from "../../../../../../components/info-block/styles";
import { formatDate } from "../../../../../../utils/date-formatter";
import { handleStatusName } from "../../../../../../utils/handle-status-name";
import { statusColor } from "../../../../../../utils/statusColor";
import { icons } from "../../../../../../icons";
import { useUi } from "../../../../../../context/ui.context";
import { kubernetesService } from "../../../../../../api";
import { extractErrorMessage } from "../../../../../../utils/error-extractor";
import { StaffClusterDetail, NodePoolDetail, KubeNode } from "../../../types";
import { useNavigate } from "react-router";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 10px;
  padding-right: 10px;
`;

const TopInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 20px;
  gap: 10px;
`;

const StateWrapper = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const GreenDot = styled.div<{ active?: string }>`
  max-width: 12px;
  max-height: 12px;
  min-width: 12px;
  min-height: 12px;
  background-color: ${({ active }) => statusColor(active)};
  border-radius: 100px;
`;

const Greentext = styled.span<{ active?: string }>`
  color: ${({ active }) => statusColor(active)};
  text-transform: capitalize;
`;

const PropContainer = styled.div`
  display: flex;
  box-sizing: border-box;
  padding: 3px 4px;
  height: 21px;
  background-color: ${({ theme }) => theme.palette[300]};
  border-radius: 4px;
  align-items: center;
`;

const Prop = styled.span`
  color: ${({ theme }) => theme.palette[850]};
  font-size: 10px;
  font-weight: 500;
`;

interface OverviewProps {
  cluster: StaffClusterDetail | null;
  onUpdate: () => void;
}

const Overview: FC<OverviewProps> = ({ cluster, onUpdate }) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { viewAlert } = useUi();

  const [viewModal, setViewModal] = useState(false);
  const [activeAnimation, setActiveAnimation] = useState(false);
  const [modalType, setModalType] = useState<any>('');
  const [alertTitle, setAlertTitle] = useState('');
  const [alertSubTitle, setAlertSubTitle] = useState<any>('');
  const [deletedNodePool, setDeletedNodePool] = useState<any>(null);
  const [editedNodePool, setEditedNodePool] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const specifications = useMemo(() => {
    if (!cluster) return [];
    return [
      {
        title: t("KUBE_API"),
        id: "api",
        value: cluster.api_address || "-",
      },
      {
        title: t("VERSION"),
        id: "version",
        value: cluster.version || "-",
      },
      {
        title: t("ID"),
        id: "id",
        value: cluster.id || "-",
        copy: true,
      },
      {
        title: t("RESTRICTION_API"),
        id: "restriction_api",
        copy: cluster.restriction_api ? true : false,
        value: cluster.restriction_api
          ? cluster.restriction_ips?.map((ip: string) => `${ip} `) || "-"
          : "False",
      },
      {
        title: t("CREATED_AT"),
        id: "created_at",
        value: formatDate(cluster.created_at),
      },
      {
        title: t("UPDATED_AT"),
        id: "updated_at",
        value: formatDate(cluster.updated_at),
      },
    ];
  }, [cluster, t]);

  const masterNodeColumns = useMemo(
    () => [
      { title: t("NAME"), key: "name" },
      { title: t("STATUS"), key: "state" },
      { title: t("KUBE_VERSION"), key: "version" },
      { title: t("FLAVOR"), key: "flavor" },
    ],
    [t]
  );

  const masterNodeRows = useMemo(
    () =>
      (cluster?.control_nodes || []).map((node) => ({
        ...node,
        name: node.name || node.id,
        state: (
          <StateWrapper>
            <GreenDot active={node.state} />
            <Greentext active={node.state}>
              {handleStatusName(node.state, t)}
            </Greentext>
          </StateWrapper>
        ),
      })),
    [cluster, t]
  );

  const nodePoolColumns = useMemo(
    () => [
      { title: t("NAME"), key: "name" },
      { title: t("NODE_COUNT"), key: "count" },
      { title: t("AUTOSCALE"), key: "autoscaleView" },
      { title: t("FLAVOR"), key: "flavor" },
      { title: t("KUBE_VERSION"), key: "version" },
      { title: "", key: "props" },
    ],
    [t]
  );

  const nodePoolItems = useMemo(
    () => [
      {
        title: t("SCALE"),
        id: "scale",
        icon: icons.Resize,
        divider: false,
        onClick: (pool: any) => {
          setEditedNodePool(pool);
          openModal("scale-pool");
        },
      },
      {
        title: t("DELETE"),
        id: "delete",
        icon: icons.DeleteIcon,
        divider: false,
        onClick: (pool: any) => {
          setDeletedNodePool(pool);
          setAlertTitle(t("NODE_POOL"));
          setAlertSubTitle([pool]);
          openModal("delete");
        },
      },
    ],
    [t]
  );

  const nodePoolRows = useMemo(
    () =>
      (cluster?.node_pools || []).map((pool) => {
        const ready = pool.nodes?.filter((n: KubeNode) => n.ready)?.length || 0;
        const total = pool.count;
        return {
          ...pool,
          customBodyRow: true,
          nodes: pool.nodes?.map((n: KubeNode, i: number) => ({
            ...n,
            poolId: pool.id,
            name: n.name || n.id,
            count: (
              <StateWrapper>
                <GreenDot active={n.state} />
                <Greentext active={n.state}>
                  {handleStatusName(n.state, t)}
                </Greentext>
              </StateWrapper>
            ),
          })),
          name: (
            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
              {pool.name || pool.id}
              {pool.is_default && (
                <PropContainer>
                  <Prop>DEFAULT</Prop>
                </PropContainer>
              )}
            </div>
          ),
          countEdit: pool.count,
          autoscaleView: pool.autoscale ? t("YES") : "-",
          version: pool.nodes?.[0]?.version || "-",
          count: (
            <StateWrapper>
              {`${ready}/${total} ready`}
            </StateWrapper>
          ),
        };
      }),
    [cluster, t]
  );

  const openModal = (modal: string) => {
    setModalType(modal);
    setViewModal(true);
    setActiveAnimation(true);
  };

  const closeModalCancel = () => {
    setViewModal(false);
    setActiveAnimation(false);
  };

  const closeModal = async () => {
    if (modalType === "delete" && deletedNodePool) {
      setLoading(true);
      try {
        await kubernetesService.deleteNodePool(deletedNodePool.id);
        setViewModal(false);
        setActiveAnimation(false);
        onUpdate();
      } catch (e: any) {
        viewAlert({
          severity: "error",
          message:
            e?.response?.data?.detail ||
            extractErrorMessage(e?.response?.data) ||
            t("ERROR_OCCURRED"),
        });
      } finally {
        setLoading(false);
      }
    } else {
      setViewModal(false);
      setActiveAnimation(false);
    }
  };

  if (!cluster) return null;

  return (
    <Container>
      <TopInfoWrapper>
        <InfoBlock
          data={specifications}
          title={t("DETAILS")}
          customStyles={{ width: "545px", flex: "auto 1", marginBottom: "0px" }}
        />
      </TopInfoWrapper>
      <InfoContainer>
        <InfoTitle style={{ marginBottom: "10px" }}>
          {t("MASTER_NODES")}
        </InfoTitle>
        <Table
          columns={masterNodeColumns}
          rows={masterNodeRows}
          hideOther
          hideCheckbox
        />
      </InfoContainer>
      <InfoContainer>
        <InfoTitle style={{ marginBottom: "10px" }}>
          {t("NODE_POOLS")}
        </InfoTitle>
        <Button
          variant="stroke"
          size="display"
          icon={icons.PlusDefault}
          customStyles={{ marginBottom: "10px" }}
          onClick={() =>
            navigate(`/openstack/clusters/${cluster.id}/create_node_pool`)
          }
          title={t("ADD_NODE_POOL")}
        />
        <Table
          columns={nodePoolColumns}
          rows={nodePoolRows}
          settings={nodePoolItems}
          hideOther
          hideCheckbox
        />
      </InfoContainer>
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        dataToEdit={editedNodePool}
        instanceId={editedNodePool?.id}
        instance={editedNodePool}
        loading={loading}
      />
    </Container>
  );
};

export default Overview;
